/**
 * SchoolRequestsPanel — de aanvragen die op een beslissing wachten.
 *
 * WAAROM DIT BOVEN HET REGISTER STAAT
 *
 * Een aanvraag betekent dat er leerkrachten wachten. Het register is een
 * naslaglijst. Zet je die in omgekeerde volgorde, dan moet een beheerder langs
 * honderd scholen scrollen om te zien of er iets te doen is — en dan gebeurt het
 * niet.
 *
 * WAAROM ELKE AANVRAAG TWEE UITGANGEN HEEFT
 *
 * "Opnemen als nieuwe school" en "toewijzen aan een bestaande" zijn geen variant
 * van elkaar. Het tweede is de reparatie van de typefout: "de regenboog",
 * "De Regenboog" en "OBS de regenboog" zijn drie aanvragen voor één school. Zou
 * alleen de eerste knop bestaan, dan krijgt het register precies de dubbelingen
 * terug die vrije tekst opleverde.
 *
 * WAAROM ER GEEN AUTOMATISCHE SAMENVOEGING IS
 *
 * Twee namen die op elkaar lijken KUNNEN twee scholen zijn — "De Regenboog" in
 * Utrecht en in Groningen zijn verschillende organisaties met verschillende
 * verwerkersovereenkomsten. Het scherm zet de kandidaten naast elkaar; de
 * beslissing blijft bij de beheerder.
 */

import React, { useState } from 'react';
import { Building2, Plus, ArrowRight, AlertTriangle, Users } from 'lucide-react';
import { upsertSchool, resolveSchoolRequest, type School, type SchoolRequest } from '../../../../data/supabase/schoolRegistry';
interface SchoolRequestsPanelProps {
  requests: SchoolRequest[];
  schools: School[];
  loading: boolean;
  error: string | null;
  onChanged: () => void;
}
export function SchoolRequestsPanel({
  requests,
  schools,
  loading,
  error,
  onChanged
}: SchoolRequestsPanelProps) {
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [assignTo, setAssignTo] = useState<Record<string, string>>({});
  const handleCreate = async (req: SchoolRequest) => {
    setBusy(req.requestedName);
    setActionError(null);
    setResult(null);
    // Opnemen én meteen toewijzen. Twee losse handelingen zouden een school
    // achterlaten waar niemand aan hangt, en dan wacht de leerkracht nog steeds.
    const created = await upsertSchool({
      name: req.requestedName
    });
    if (!created.ok || !created.id) {
      setBusy(null);
      setActionError(created.message ?? 'De school is niet aangemaakt.');
      return;
    }
    const linked = await resolveSchoolRequest(req.requestedName, created.id);
    setBusy(null);
    if (!linked.ok) {
      setActionError(`De school is aangemaakt, maar de ${req.teacherCount} wachtende leerkracht(en) zijn er niet aan gekoppeld. ${linked.message ?? ''}`);
      onChanged();
      return;
    }
    setResult(`"${req.requestedName}" staat in het register en ${linked.updated} leerkracht(en) zijn eraan gekoppeld. De school staat op onbevestigd — verifiëren is een aparte stap hieronder.`);
    onChanged();
  };
  const handleAssign = async (req: SchoolRequest) => {
    const target = assignTo[req.requestedName];
    if (!target) return;
    setBusy(req.requestedName);
    setActionError(null);
    setResult(null);
    const res = await resolveSchoolRequest(req.requestedName, target);
    setBusy(null);
    if (!res.ok) {
      setActionError(res.message ?? 'De aanvraag is niet toegewezen.');
      return;
    }
    if (res.updated === 0) {
      // Nul is geen fout, maar mag niet als succes voelen: dan denkt de
      // beheerder iets te hebben opgelost wat er niet meer was.
      setResult('Er was niets meer toe te wijzen — deze aanvraag is waarschijnlijk al door iemand anders afgehandeld.');
      onChanged();
      return;
    }
    const school = schools.find(s => s.id === target);
    setResult(`${res.updated} leerkracht(en) staan nu bij ${school?.name ?? 'de gekozen school'}. De aanvraagtekst is gewist.`);
    onChanged();
  };

  /** Scholen met een naam die op de aanvraag lijkt, als suggestie. */
  const suggestionsFor = (name: string): School[] => {
    const norm = name.toLowerCase().replace(/\b(obs|bs|rkbs|pcbs|basisschool|school)\b/g, '').replace(/[^a-z0-9]/g, '');
    if (norm.length < 3) return [];
    return schools.filter(s => {
      const other = s.name.toLowerCase().replace(/\b(obs|bs|rkbs|pcbs|basisschool|school)\b/g, '').replace(/[^a-z0-9]/g, '');
      return other.length >= 3 && (other.includes(norm) || norm.includes(other));
    }).slice(0, 3);
  };
  return <section aria-labelledby="requests-heading" className="rounded-xl border border-slate-200 bg-white p-5" data-id="element-7361" data-mp="1e6bxtyfv1i89">
      <div className="flex items-start justify-between gap-4" data-id="element-7362" data-mp="1j55a5njev36u">
        <div data-id="element-7363" data-mp="1o2yj2oc3mbat">
          <h2 id="requests-heading" className="text-base font-bold text-slate-900" data-id="element-7364" data-mp="1o36yamvp1sp6">
            Aangevraagde scholen
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600" data-id="element-7365" data-mp="1svhubenbcucg">
            Scholen die een leerkracht zelf heeft ingetikt omdat ze niet in het
            register stonden. Zolang een aanvraag hier staat, is de schoolnaam
            van die leerkracht een bewering en niet iets wat je kunt natrekken.
          </p>
        </div>
        {requests.length > 0 && <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900" data-id="element-7366" data-mp="nwbizeqhmda3">
            {requests.length} open
          </span>}
      </div>

      {loading && <p className="mt-4 text-sm text-slate-500" data-id="element-7367" data-mp="dxm2nqe8p0vy">Aanvragen ophalen…</p>}

      {error && <p role="alert" className="mt-4 flex gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-900" data-id="element-7368" data-mp="9v8vcxjugux2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" data-id="element-7369" data-mp="55274yt8vcfc" />
          <span data-id="element-7370" data-mp="5fv2sp5k284">
            <strong data-id="element-7371" data-mp="4wd5bzi5du3">{error}</strong> Dit is geen lege lijst — er kunnen aanvragen
            openstaan die je nu niet ziet.
          </span>
        </p>}

      {result && <p role="status" className="mt-4 rounded-lg bg-emerald-50 p-3 text-sm leading-relaxed text-emerald-900" data-id="element-7372" data-mp="3ra5wwlwwkvv9">
          {result}
        </p>}

      {actionError && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm leading-relaxed text-rose-900" data-id="element-7373" data-mp="3iclqkyk6o9ds">
          {actionError}
        </p>}

      {!loading && !error && requests.length === 0 && <p className="mt-4 rounded-lg bg-slate-50 p-4 text-sm leading-relaxed text-slate-600" data-id="element-7374" data-mp="3cqcts3yuozb7">
          Geen openstaande aanvragen. Elke leerkracht met een school heeft er een
          uit het register gekozen.
        </p>}

      {requests.length > 0 && <ul className="mt-4 space-y-3" data-id="element-7375" data-mp="33qgbql6yuyp4">
          {requests.map(req => {
        const suggestions = suggestionsFor(req.requestedName);
        const isBusy = busy === req.requestedName;
        return <li key={req.requestedName} className="rounded-lg border border-slate-200 p-4" data-id="element-7376" data-mp="3vhm6em3x4470">
                <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-7377" data-mp="3hmac2ftocfqm">
                  <div data-id="element-7378" data-mp="3mlmyx0kjpmt7">
                    <p className="font-bold text-slate-900" data-id="element-7379" data-mp="3ml4uhgnqc2a7">
                      {req.requestedName}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500" data-id="element-7380" data-mp="iywf32qag0wk">
                      <Users className="h-3.5 w-3.5" aria-hidden="true" data-id="element-7381" data-mp="scy3zdx42j8p" />
                      {req.teacherCount === 1 ? '1 leerkracht wacht' : `${req.teacherCount} leerkrachten wachten`}
                      {req.firstSeenAt && <> · eerste aanvraag {new Date(req.firstSeenAt).toLocaleDateString('nl-NL')}</>}
                    </p>
                  </div>
                  <button onClick={() => void handleCreate(req)} disabled={isBusy} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60" data-id="element-7382" data-mp="x4oilw32zt5x">
                    <Plus className="h-4 w-4" aria-hidden="true" data-id="element-7383" data-mp="jcrxibztyjj1" />
                    {isBusy ? 'Bezig…' : 'Opnemen als nieuwe school'}
                  </button>
                </div>

                {suggestions.length > 0 && <p className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900" data-id="element-7384" data-mp="1uqlzjp1tbqfr">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-7385" data-mp="1ps28xbc9xqh2" />
                    <span data-id="element-7386" data-mp="3kan6uszgcw01">
                      Er staat al iets vergelijkbaars in het register:{' '}
                      <strong data-id="element-7387" data-mp="3o8b5a60ct6wo">
                        {suggestions.map(s => s.name).join('", "')}
                      </strong>
                      . Dat kan dezelfde school zijn met een andere spelling —
                      of een andere school met een gelijkende naam. Controleer de
                      plaats voordat je samenvoegt.
                    </span>
                  </p>}

                {schools.length > 0 && <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-7388" data-mp="butsokslfwm5">
                    <label className="text-xs font-bold uppercase tracking-wide text-slate-500" htmlFor={`assign-${req.requestedName}`} data-id="element-7389" data-mp="fvdmbedyki3j">
                      Of toewijzen aan
                    </label>
                    <select id={`assign-${req.requestedName}`} value={assignTo[req.requestedName] ?? ''} onChange={e => setAssignTo(prev => ({
              ...prev,
              [req.requestedName]: e.target.value
            }))} className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900" data-id="element-7390" data-mp="2hns144l7h6fq">
                      <option value="" data-id="element-7391" data-mp="2a8zklqerxhdi">Kies een bestaande school…</option>
                      {schools.map(s => <option key={s.id} value={s.id} data-id="element-7392" data-mp="259d8jkaom76a">
                          {s.name}
                          {s.city ? ` — ${s.city}` : ''}
                          {s.status !== 'verified' ? ' (onbevestigd)' : ''}
                        </option>)}
                    </select>
                    <button onClick={() => void handleAssign(req)} disabled={isBusy || !assignTo[req.requestedName]} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50" data-id="element-7393" data-mp="2t23ftg7n42a8">
                      <ArrowRight className="h-4 w-4" aria-hidden="true" data-id="element-7394" data-mp="1ckxvv4f0w0y4" />
                      Toewijzen
                    </button>
                  </div>}

                {schools.length === 0 && <p className="mt-3 flex gap-2 text-xs leading-relaxed text-slate-500" data-id="element-7395" data-mp="25igauum72usv">
                    <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-7396" data-mp="21i64vhyek4e0" />
                    Het register is nog leeg, dus er is niets om aan toe te
                    wijzen. Neem deze school op als eerste.
                  </p>}
              </li>;
      })}
        </ul>}
    </section>;
}